import React, { useEffect, useState } from "react";
import type { Feature } from "geojson";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Select from "@components/Select";
import Spinner from "@components/Spinner";
import { useLang } from "@src/hooks/useLang";
import { observer } from "mobx-react-lite";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import {
  parseBoundaryFile,
  applyPropertyAsName,
  applySequentialNames,
  BoundaryParseError,
  TParsedBoundaryFile,
} from "@src/services/boundaryFileService";
import BoundaryPreviewMap from "./BoundaryPreviewMap";

interface IProps {
  visible: boolean;
  onSubmit: (features: Feature[], cropId: string) => Promise<unknown>;
  onClose: () => void;
}

// Same wizard as the landing-page converter (greengrowth.tech/converter):
// upload → confirm existing names / pick a name attribute / sequential
// fallback → preview on a map → import.
type TStep = "select" | "parsing" | "confirmNames" | "selectProperty" | "ready";

const UploadFromFileDialog: React.FC<IProps> = ({
  visible,
  onClose,
  onSubmit,
}) => {
  const lang = useLang();
  const fieldsLang = lang.fields;
  const vm = useFieldsScreenVM();

  const [step, setStep] = useState<TStep>("select");
  const [cropId, setCropId] = useState<string>("");
  const [parsed, setParsed] = useState<TParsedBoundaryFile | null>(null);
  const [finalFeatures, setFinalFeatures] = useState<Feature[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [importing, setImporting] = useState(false);

  // Default to first crop when crops are loaded
  useEffect(() => {
    if (vm.crops.length > 0 && !cropId) {
      setCropId(vm.crops[0].id);
    }
  }, [vm.crops, cropId]);

  // Fresh wizard every time the dialog opens
  useEffect(() => {
    if (visible) {
      setStep("select");
      setParsed(null);
      setFinalFeatures([]);
      setSelectedProperty(null);
      setError("");
      setImporting(false);
    }
  }, [visible]);

  const errorMessage = (e: unknown): string => {
    if (e instanceof BoundaryParseError) {
      const localized = fieldsLang[`uploadError_${e.code}`];
      if (typeof localized === "string") {
        const showDetail =
          e.code === "singleShapefileComponent" ||
          e.code === "missingShapefileParts";
        return showDetail && e.detail ? `${localized} (${e.detail})` : localized;
      }
      return e.message;
    }
    return e instanceof Error ? e.message : String(e);
  };

  const onSetFile = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0];
    target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setError("");
    setStep("parsing");
    try {
      const result = await parseBoundaryFile(file);
      setParsed(result);
      if (result.hasNames && result.nameExamples.length > 0) {
        setStep("confirmNames");
      } else if (result.properties.length > 0) {
        setStep("selectProperty");
      } else {
        setFinalFeatures(applySequentialNames(result.features));
        setStep("ready");
      }
    } catch (e) {
      setError(errorMessage(e));
      setStep("select");
    }
  };

  const handleConfirmNames = () => {
    if (!parsed) return;
    setFinalFeatures(parsed.features);
    setStep("ready");
  };

  const handleRejectNames = () => {
    if (!parsed) return;
    if (parsed.properties.length > 0) {
      setStep("selectProperty");
    } else {
      setFinalFeatures(applySequentialNames(parsed.features));
      setStep("ready");
    }
  };

  const handleApplyProperty = () => {
    if (!parsed || !selectedProperty) return;
    setFinalFeatures(applyPropertyAsName(parsed.features, selectedProperty));
    setStep("ready");
  };

  const handleSequential = () => {
    if (!parsed) return;
    setFinalFeatures(applySequentialNames(parsed.features));
    setStep("ready");
  };

  const handleImport = async () => {
    if (finalFeatures.length === 0 || !cropId) return;
    setImporting(true);
    try {
      await onSubmit(finalFeatures, cropId);
    } finally {
      setImporting(false);
    }
  };

  const startOver = () => {
    setStep("select");
    setParsed(null);
    setFinalFeatures([]);
    setSelectedProperty(null);
    setError("");
  };

  const renderSelect = () => (
    <>
      <Text style={{ whiteSpace: "pre-wrap" }}>
        {fieldsLang.uploadDialogText}
      </Text>
      <SizedBox height={16} />
      <Text size={14}>
        <a
          href="https://www.greengrowth.tech/converter"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#68ad18" }}
        >
          {fieldsLang.uploadConverterLink}
        </a>
      </Text>
      <SizedBox height={12} />
      {/* This importer creates FIELDS. Customers regularly bring a prescription
          or yield map here instead; rather than guessing at the file (which
          wrongly rejected real boundary exports before — bug-026), say where
          those belong. */}
      <Text size={13} color="#6B7280" style={{ whiteSpace: "pre-wrap" }}>
        {fieldsLang.uploadDataLayerHint}
      </Text>
      <SizedBox height={24} />
      <Select
        label={fieldsLang.newFieldCrop}
        value={cropId}
        onChange={setCropId}
        options={vm.crops.map((crop) => ({
          title: crop.name,
          value: crop.id,
        }))}
      />
      <SizedBox height={24} />
      <input type="file" accept=".kml,.kmz,.zip" onChange={onSetFile} />
      {error && (
        <>
          <SizedBox height={16} />
          <Text size={14} color="#e53e3e" style={{ whiteSpace: "pre-wrap" }}>
            {error}
          </Text>
        </>
      )}
      <SizedBox height={32} />
      <Row justifyContent="flex-end">
        <Button onClick={onClose} kind="outline">
          {fieldsLang.uploadDialogCancelButton}
        </Button>
      </Row>
    </>
  );

  const renderParsing = () => (
    <Column crossAxisSize="max" alignItems="center" style={{ padding: 32 }}>
      <Spinner />
      <SizedBox height={12} />
      <Text size={14}>{fieldsLang.uploadParsing}</Text>
    </Column>
  );

  const renderConfirmNames = () => (
    <>
      <Text weight={500}>
        {fieldsLang.uploadConfirmNamesTitle}
      </Text>
      <SizedBox height={12} />
      <Column crossAxisSize="max" style={{ gap: 6 }}>
        {parsed?.nameExamples.map((n, i) => (
          <Text key={i} size={14}>
            {i + 1}. <b>{n}</b>
          </Text>
        ))}
      </Column>
      <SizedBox height={16} />
      {parsed && <BoundaryPreviewMap features={parsed.features} />}
      <SizedBox height={24} />
      <Row justifyContent="flex-end" style={{ gap: 8 }}>
        <Button kind="outline" onClick={handleRejectNames}>
          {fieldsLang.uploadConfirmNo}
        </Button>
        <Button onClick={handleConfirmNames}>
          {fieldsLang.uploadConfirmYes}
        </Button>
      </Row>
    </>
  );

  const renderSelectProperty = () => (
    <>
      <Text weight={500}>
        {fieldsLang.uploadSelectPropertyTitle}
      </Text>
      <SizedBox height={12} />
      <Column
        crossAxisSize="max"
        style={{ gap: 8, maxHeight: 260, overflowY: "auto" }}
      >
        {parsed?.properties.map((prop) => {
          const selected = selectedProperty === prop.key;
          return (
            <div
              key={prop.key}
              onClick={() => setSelectedProperty(prop.key)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: selected ? "2px solid #68ad18" : "1px solid #e5e7eb",
                cursor: "pointer",
              }}
            >
              <Text size={14} weight={500}>
                {prop.key}
              </Text>
              <Text size={12} color="#6B7280">
                {fieldsLang.uploadSelectPropertyExample +
                  ": " +
                  (prop.example || "—")}
              </Text>
            </div>
          );
        })}
      </Column>
      <SizedBox height={16} />
      <Row justifyContent="space-between" style={{ gap: 8 }}>
        <Button kind="outline" onClick={handleSequential}>
          {fieldsLang.uploadNoNamesButton}
        </Button>
        <Button onClick={handleApplyProperty} disabled={!selectedProperty}>
          {fieldsLang.uploadSelectPropertyApply}
        </Button>
      </Row>
    </>
  );

  const renderReady = () => (
    <>
      <Text weight={500}>
        {fieldsLang.uploadReadyTitle +
          ": " +
          finalFeatures.length +
          " " +
          fieldsLang.uploadReadyFields}
      </Text>
      <SizedBox height={12} />
      <BoundaryPreviewMap features={finalFeatures} />
      <SizedBox height={16} />
      <Select
        label={fieldsLang.newFieldCrop}
        value={cropId}
        onChange={setCropId}
        options={vm.crops.map((crop) => ({
          title: crop.name,
          value: crop.id,
        }))}
      />
      <SizedBox height={24} />
      <Row justifyContent="space-between" style={{ gap: 8 }}>
        <Button kind="outline" onClick={startOver} disabled={importing}>
          {fieldsLang.uploadStartOver}
        </Button>
        <Button
          onClick={handleImport}
          disabled={finalFeatures.length === 0 || !cropId || importing}
        >
          {importing
            ? fieldsLang.uploadImporting
            : fieldsLang.uploadDialogSaveButton}
        </Button>
      </Row>
    </>
  );

  return (
    <Dialog
      title={fieldsLang.uploadButton}
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 560 }}
    >
      {step === "select" && renderSelect()}
      {step === "parsing" && renderParsing()}
      {step === "confirmNames" && renderConfirmNames()}
      {step === "selectProperty" && renderSelectProperty()}
      {step === "ready" && renderReady()}
    </Dialog>
  );
};
export default observer(UploadFromFileDialog);
